import { supabase } from './supabase';
import { devLog } from '@/lib/utils';
import { errorDeEdgeFunction } from '@/lib/errorDeEdgeFunction';

/**
 * Storage frontend — embudo ÚNICO de uploads (regla R2-first).
 *
 * Todo archivo que sube el navegador pasa por `uploadAsset()`, que pide una
 * presigned URL a la Edge Function `storage-presign` y hace PUT directo a
 * Cloudflare R2 (browser → R2, sin proxy, egress $0).
 *
 * PROHIBIDO subir a Supabase Storage desde el frontend (regla ESLint).
 * Ver docs/architecture/STORAGE-R2-RULE.md
 */

/** Categorías públicas de storefront que el frontend puede subir. */
export type AssetCategory = 'products' | 'theme' | 'branding' | 'logistics' | 'media' | 'messages' | 'documents';

const IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/avif',
  'image/svg+xml',
];

const DEFAULT_MAX_BYTES = 10 * 1024 * 1024; // 10 MB

interface PresignResponse {
  uploadUrl: string;
  publicUrl: string;
  path: string;
  method: 'PUT';
  headers: Record<string, string>;
  expiresInSeconds: number;
}

export interface UploadAssetOptions {
  category: AssetCategory;
  /** Identificador semántico (ej. productId, 'logo', courierId). */
  identifier: string;
  /**
   * Tenant activo. La Edge Function verifica acceso. Si se omite, el servidor
   * cae al tenant del usuario (legacy single-tenant). Pásalo siempre que lo
   * tengas para ser multi-tenant safe.
   */
  tenantId?: string;
  /** Tipos MIME permitidos. Default: imágenes. */
  allowedTypes?: string[];
  /** Tamaño máximo en bytes. Default 10 MB. */
  maxBytes?: number;
  /**
   * Añadir sufijo único al path para cache-busting del CDN. Default `true`.
   * Pasar `false` solo si necesitas una URL determinista (rara vez).
   */
  unique?: boolean;
}

export interface UploadedAsset {
  /** URL pública vía CDN (media.smarty.lat). Esto es lo que se guarda en la BD. */
  url: string;
  /** Path canónico dentro del bucket R2. */
  path: string;
}

/**
 * Convierte un data URL (base64) a File para poder subirlo.
 */
export function dataURLToFile(dataUrl: string, defaultName = 'image.jpg'): File {
  const [header, base64] = dataUrl.split(',');
  const mime = header.match(/data:([^;]+);/)?.[1] || 'image/jpeg';
  const ext = mime.split('/')[1] === 'jpeg' ? 'jpg' : mime.split('/')[1] || 'jpg';
  const bytes = atob(base64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  const blob = new Blob([arr], { type: mime });
  return new File([blob], defaultName.replace(/\.[^.]+$/, '') + '.' + ext, { type: mime });
}

/**
 * Optimización de imagen al SUBIR (Opción C — perf storefront, ver
 * docs/plan/PLAN-PERF-IMAGENES-STOREFRONT.md): redimensiona a `maxWidth` y
 * convierte a WebP con el Canvas del navegador, UNA sola vez. La imagen guardada
 * ya queda liviana → se sirve estática y rápida (sin servicio, sin per-request).
 *
 * Robusto: SVG/GIF se dejan intactos (vectorial/animación), si el WebP saliera
 * más grande que el original conserva el original, y ante cualquier fallo cae
 * al archivo original (nunca rompe la subida).
 *
 * `type`: formato de salida (default WebP, el mejor para la web). Usá 'image/jpeg'
 * cuando el destino NO sea el navegador — WhatsApp trata al WebP como sticker, así
 * que las imágenes que manda el agente deben ser JPEG. Con un `type` explícito la
 * conversión NO se saltea aunque el resultado pese más: ahí el formato es un
 * requisito de compatibilidad, no una optimización.
 */
export async function optimizeImageForUpload(
  file: File,
  opts: { maxWidth?: number; quality?: number; type?: 'image/webp' | 'image/jpeg' } = {}
): Promise<File> {
  const maxWidth = opts.maxWidth ?? 1600;
  const quality = opts.quality ?? 0.82;
  const outType = opts.type ?? 'image/webp';
  const formatRequired = opts.type != null && file.type !== opts.type;
  if (typeof document === 'undefined' || typeof createImageBitmap === 'undefined') return file;
  if (file.type === 'image/svg+xml' || file.type === 'image/gif') return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = bitmap.width > maxWidth ? maxWidth / bitmap.width : 1;
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, outType, quality)
    );
    if (!blob || blob.size === 0) return file;
    if (!formatRequired && blob.size >= file.size) return file;
    const ext = outType === 'image/jpeg' ? '.jpg' : '.webp';
    const name = file.name.replace(/\.[^.]+$/, '') + ext;
    return new File([blob], name, { type: outType });
  } catch {
    return file;
  }
}

/**
 * Sube un archivo a Cloudflare R2 vía presigned PUT.
 *
 * @returns URL pública (CDN) + path canónico.
 * @throws si el tipo/tamaño no es válido o si la subida falla.
 */
export async function uploadAsset(file: File | Blob, opts: UploadAssetOptions): Promise<UploadedAsset> {
  const contentType = file.type || 'application/octet-stream';
  const allowed = opts.allowedTypes ?? IMAGE_TYPES;
  const maxBytes = opts.maxBytes ?? DEFAULT_MAX_BYTES;

  if (allowed.length > 0 && !allowed.includes(contentType)) {
    throw new Error(`Tipo de archivo no permitido (${contentType}). Permitidos: ${allowed.join(', ')}`);
  }
  if (file.size > maxBytes) {
    throw new Error(`El archivo es demasiado grande. Máximo ${Math.round(maxBytes / 1024 / 1024)}MB`);
  }

  const suffix = opts.unique === false ? '' : `_${Date.now().toString(36)}`;
  const identifier = `${opts.identifier}${suffix}`;

  // 1. Presigned URL desde la Edge Function (el JWT se adjunta automáticamente).
  const { data, error } = await supabase.functions.invoke<PresignResponse>('storage-presign', {
    body: {
      category: opts.category,
      identifier,
      contentType,
      sizeBytes: file.size,
      ...(opts.tenantId ? { tenant_id: opts.tenantId } : {}),
    },
  });

  if (error || !data?.uploadUrl) {
    devLog.error('storage-presign error:', error);
    throw new Error(`No se pudo preparar la subida: ${error?.message ?? 'sin uploadUrl'}`);
  }

  // 2. PUT directo a R2 con los headers firmados que devolvió la fn.
  const putRes = await fetch(data.uploadUrl, {
    method: 'PUT',
    headers: data.headers,
    body: file,
  });

  if (!putRes.ok) {
    const errBody = await putRes.text().catch(() => '');
    devLog.error('R2 PUT failed:', putRes.status, errBody.slice(0, 200));
    throw new Error(`Error al subir el archivo (${putRes.status})`);
  }

  // Registrar en el índice de medios (best-effort — no rompe el upload).
  // Es la fuente de verdad de la Biblioteca de Medios (R2 no tiene list público).
  void registerMediaAsset({
    tenantId: opts.tenantId,
    key: data.path,
    url: data.publicUrl,
    contentType,
    sizeBytes: file.size,
    category: opts.category,
    filename: file instanceof File ? file.name : undefined,
  });

  return { url: data.publicUrl, path: data.path };
}

/**
 * Subir imagen de producto a R2, con **optimización previa** (resize + WebP,
 * perf storefront). Combina lo mejor: la imagen se optimiza una vez en el
 * navegador (liviana) y se sube a R2 (egress $0).
 *
 * @param file Archivo de imagen a subir
 * @param productId ID del producto (UUID)
 * @param tenantId ID del tenant (UUID)
 * @param index Índice para galería (0 = principal). Opcional.
 * @returns URL pública (CDN) de la imagen subida
 */
export async function uploadProductImage(
  file: File,
  productId: string,
  tenantId: string,
  index?: number
): Promise<string> {
  // Opción C (perf): optimizar (resize + WebP) ANTES de subir → hero 700KB → ~60KB.
  const optimized = await optimizeImageForUpload(file, { maxWidth: 1600, quality: 0.82 });
  const identifier = typeof index === 'number' ? `${productId}_${index}` : productId;
  const { url } = await uploadAsset(optimized, {
    category: 'products',
    identifier,
    tenantId,
    allowedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
    maxBytes: 25 * 1024 * 1024, // tope R2 presign (la imagen ya viene optimizada)
  });
  return url;
}

/**
 * Delete-hook: borra un asset de R2 cuando el SaaS lo elimina o reemplaza.
 *
 * Es **seguro**: el guard del backend NO borra si la imagen sigue referenciada.
 * Y nunca lanza: un fallo de limpieza no debe romper el guardado.
 *
 * Tres respuestas posibles, y conviene distinguirlas al mostrarlas:
 *   • `deleted` — se fue de R2.
 *   • `skipped` — el guard la conservó (sigue en uso en otro lado). `reason` dice dónde.
 *   • `queued`  — R2 falló y quedó encolada; el worker reintenta con DLQ. NO es un error.
 *
 * Llamar DESPUÉS de persistir el nuevo estado: el guard decide mirando la base.
 */
export async function deleteAsset(
  url: string,
  tenantId?: string,
): Promise<{ deleted: boolean; queued?: boolean; reason?: string }> {
  if (!url || !/^https?:\/\//.test(url)) return { deleted: false, reason: 'no_url' };
  try {
    const { data } = await supabase.functions.invoke<{ deleted?: boolean; skipped?: boolean; queued?: boolean; reason?: string }>(
      'storage-delete',
      { body: { url, ...(tenantId ? { tenant_id: tenantId } : {}) } },
    );
    return { deleted: !!data?.deleted, queued: !!data?.queued, reason: data?.reason };
  } catch (e) {
    devLog.warn('deleteAsset (best-effort) falló:', e);
    return { deleted: false, reason: 'error' };
  }
}

// ─── La papelera (20261013100000) ─────────────────────────────────────────────
//
// Eliminar ya no es para siempre: el archivo queda 30 días en la papelera y se devuelve a su lugar, con la misma
// dirección, así que el producto o el logo que lo usaba vuelve a mostrarlo.

export interface ArchivoEnPapelera {
  id: string;
  nombre: string;
  /** Quién lo borró: biblioteca (una persona), cola (un reemplazo), barrido (la limpieza)… */
  origen: string;
  borrado_en: string;
  expira_en: string;
}

/** Lo que se puede restaurar todavía: no restaurado y dentro de los 30 días. Lo más reciente primero. */
export async function listarPapelera(tenantId: string): Promise<ArchivoEnPapelera[]> {
  const { data, error } = await supabase
    .from('media_papelera')
    .select('id, key, origen, borrado_en, expira_en, indice')
    .eq('tenant_id', tenantId)
    .is('restaurado_en', null)
    .gt('expira_en', new Date().toISOString())
    .order('borrado_en', { ascending: false })
    .limit(200);
  if (error) throw error;
  return (data ?? []).map((r: { id: string; key: string; origen: string; borrado_en: string; expira_en: string; indice: Array<{ filename?: string | null }> | null }) => ({
    id: r.id,
    nombre: r.indice?.[0]?.filename || r.key.split('/').pop() || r.key,
    origen: r.origen,
    borrado_en: r.borrado_en,
    expira_en: r.expira_en,
  }));
}

/** Devuelve un archivo de la papelera a su lugar. Lanza con el motivo real si no se pudo. */
export async function restaurarDePapelera(id: string, tenantId?: string): Promise<void> {
  const { data, error } = await supabase.functions.invoke<{ restored?: boolean }>('storage-delete', {
    body: { accion: 'restaurar', id, ...(tenantId ? { tenant_id: tenantId } : {}) },
  });
  if (error) throw new Error(await errorDeEdgeFunction(error, 'No se pudo restaurar.'));
  if (!data?.restored) throw new Error('No se pudo restaurar.');
}

/** Un archivo que la tienda usa y no existe (la alarma diaria, 20261013110000). */
export interface ArchivoQueFalta {
  /** En palabras de quien lo tiene que volver a subir («Imagen de una oferta»). */
  que_es: string;
  nombre: string;
}

/**
 * Lo que la tienda usa y falta, y se puede volver a subir. Los adjuntos de chats y las fotos de contactos quedan
 * fuera: no hay qué subir (los chats viejos no vuelven y las fotos de contacto las trae WhatsApp).
 */
export async function listarFaltantes(tenantId: string): Promise<ArchivoQueFalta[]> {
  const { data, error } = await supabase
    .from('media_faltantes')
    .select('key, que_es')
    .eq('tenant_id', tenantId)
    .is('resuelto_en', null)
    .not('donde', 'like', 'wa\\_%')
    .order('que_es')
    .limit(100);
  if (error) throw error;
  return (data ?? []).map((f: { key: string; que_es: string }) => ({ que_es: f.que_es, nombre: f.key.split('/').pop() || f.key }));
}

// ─── Biblioteca de Medios (índice media_assets, Fase A.2) ────────────────────

export interface MediaAsset {
  id: string;
  tenant_id: string;
  key: string;
  url: string;
  content_type: string | null;
  size_bytes: number;
  category: string | null;
  filename: string | null;
  created_at: string;
}

interface RegisterMediaInput {
  tenantId?: string;
  key: string;
  url: string;
  contentType?: string;
  sizeBytes: number;
  category?: string;
  filename?: string;
}

/**
 * Registra un asset subido en el índice `media_assets` (fuente de verdad de la
 * Biblioteca de Medios, ya que R2 no expone list público). Best-effort: nunca
 * lanza. Upsert por (tenant_id, key) → re-subir la misma key no duplica.
 */
async function registerMediaAsset(a: RegisterMediaInput): Promise<void> {
  if (!a.tenantId) return; // sin tenant no hay scoping RLS
  try {
    await supabase.from('media_assets').upsert(
      {
        tenant_id: a.tenantId,
        key: a.key,
        url: a.url,
        content_type: a.contentType ?? null,
        size_bytes: a.sizeBytes,
        category: a.category ?? null,
        filename: a.filename ?? null,
      },
      { onConflict: 'tenant_id,key' },
    );
  } catch (e) {
    devLog.warn('registerMediaAsset (best-effort) falló:', e);
  }
}

/** Lista los medios del tenant desde el índice (para la galería). */
export async function listMediaAssets(tenantId: string): Promise<MediaAsset[]> {
  const { data, error } = await supabase
    .from('media_assets')
    .select('*')
    .eq('tenant_id', tenantId)
    .order('created_at', { ascending: false });
  if (error) {
    devLog.error('listMediaAssets error:', error);
    return [];
  }
  return (data ?? []) as MediaAsset[];
}
